import * as React from 'react';
import { createLink, LinkComponent } from '@tanstack/react-router';
import {
  mergeProps,
  useFocusRing,
  useHover,
  useLink,
  useObjectRef,
} from 'react-aria';
import type { AriaLinkOptions } from 'react-aria';

import { twMerge } from 'tailwind-merge';
import { Typography } from '../../../ui';

interface RACLinkProps extends Omit<AriaLinkOptions, 'href'> {
  children?: React.ReactNode;
  className?: string;
}

const RACLinkComponent = React.forwardRef<HTMLAnchorElement, RACLinkProps>(
  (props, forwardedRef) => {
    const { className, ...rest } = props;
    const ref = useObjectRef(forwardedRef);

    const { isPressed, linkProps } = useLink(rest, ref);
    const { isHovered, hoverProps } = useHover(rest);
    const { isFocusVisible, isFocused, focusProps } = useFocusRing(rest);

    return (
      <a
        {...mergeProps(linkProps, hoverProps, focusProps, rest)}
        ref={ref}
        data-hovered={isHovered || undefined}
        data-pressed={isPressed || undefined}
        data-focus-visible={isFocusVisible || undefined}
        data-focused={isFocused || undefined}
        aria-disabled={rest?.isDisabled}
        data-disabled={rest.isDisabled}
        className={twMerge(className)}
      >
        <Typography
          as="span"
          variant="body-xs"
          weight="medium"
          className="text-info300"
        >
          {rest.children}
        </Typography>
      </a>
    );
  },
);

const CreatedLinkComponent = createLink(RACLinkComponent);

export const Link: LinkComponent<typeof RACLinkComponent> = (props) => {
  return <CreatedLinkComponent {...props} />;
};
